import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

// Admin COD settlement: see each delivery partner's unsettled (held) COD cash and mark
// individual orders — or all of a partner's — as paid/settled when the rider deposits the
// cash offline/in person. Backend: /admin/cod-settlement/*.
const theme = { primary: '#FF6961', text: '#0F172A', sub: '#64748B', bg: '#F8FAFC', border: '#E2E8F0' };
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, fontWeight: 700, color: theme.sub, borderBottom: `2px solid ${theme.border}`, whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', fontSize: 13, borderBottom: `1px solid #f1f5f9`, color: theme.text };
const btn = { padding: '8px 14px', borderRadius: 8, border: `1px solid ${theme.border}`, background: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 };

export default function CodSettlement() {
  const [partners, setPartners] = useState([]);
  const [summary, setSummary] = useState({ totalPending: 0, partnerCount: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 5000); };

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminAPI.codSettlementPartners();
      const d = r.data?.data || {};
      setPartners(d.partners || []);
      setSummary({ totalPending: d.totalPending || 0, partnerCount: d.partnerCount || 0 });
    } catch (e) {
      flash('error', 'Failed to load: ' + (e.response?.data?.message || e.message));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  const openPartner = async (partnerId) => {
    if (expanded === partnerId) { setExpanded(null); setOrders([]); setSelected({}); return; }
    setExpanded(partnerId); setOrders([]); setSelected({}); setOrdersLoading(true);
    try {
      const r = await adminAPI.codSettlementOrders(partnerId);
      setOrders(r.data?.data?.orders || []);
    } catch (e) {
      flash('error', 'Failed to load orders: ' + (e.response?.data?.message || e.message));
    } finally { setOrdersLoading(false); }
  };

  const toggle = (orderId) => setSelected((s) => ({ ...s, [orderId]: !s[orderId] }));
  const allSelected = orders.length > 0 && orders.every((o) => selected[o.orderId]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else { const s = {}; orders.forEach((o) => { s[o.orderId] = true; }); setSelected(s); }
  };

  const selectedIds = orders.filter((o) => selected[o.orderId]).map((o) => o.orderId);
  const selectedTotal = orders.filter((o) => selected[o.orderId]).reduce((s, o) => s + (o.amount || 0), 0);

  const markPaid = async (ids) => {
    if (!ids.length) { flash('error', 'Select at least one order.'); return; }
    const total = orders.filter((o) => ids.includes(o.orderId)).reduce((s, o) => s + (o.amount || 0), 0);
    if (!window.confirm(`Mark ${ids.length} order(s) — ${inr(total)} — as PAID / settled?\n\nThis records that the rider has deposited this cash and clears their cash-in-hand for these orders.`)) return;
    setBusy(true); setMsg({ type: '', text: '' });
    try {
      const r = await adminAPI.codSettlementMarkPaid({ orderIds: ids, partnerId: expanded });
      flash('success', r.data?.message || `Marked ${r.data?.data?.settled || 0} settled.`);
      const o = await adminAPI.codSettlementOrders(expanded);
      setOrders(o.data?.data?.orders || []);
      setSelected({});
      loadPartners();
    } catch (e) {
      flash('error', e.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg }}>
      <Navbar />
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 16px 60px' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: theme.text, marginBottom: 4 }}>💵 COD Settlement</h1>
        <p style={{ color: theme.sub, marginBottom: 18 }}>Mark each delivery partner's held COD orders as paid when they deposit the cash. Settling clears the rider's cash-in-hand (and lifts any revoke-hold once they reach ₹0).</p>

        {msg.text && (
          <div style={{ padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 500, background: msg.type === 'success' ? '#D1FAE5' : '#FEE2E2', color: msg.type === 'success' ? '#065F46' : '#991B1B' }}>{msg.text}</div>
        )}

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
          <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 12, padding: '14px 18px', minWidth: 180 }}>
            <div style={{ color: theme.sub, fontSize: 13 }}>Total COD held</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: theme.text }}>{inr(summary.totalPending)}</div>
            <div style={{ fontSize: 12, color: '#9ca3af' }}>across {summary.partnerCount} partner(s)</div>
          </div>
          <button style={{ ...btn, alignSelf: 'flex-end' }} onClick={loadPartners}>↻ Refresh</button>
        </div>

        {loading ? (
          <div style={{ color: theme.sub }}>Loading…</div>
        ) : partners.length === 0 ? (
          <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 12, padding: 24, color: theme.sub }}>No delivery partners are holding unsettled COD right now. 🎉</div>
        ) : (
          <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead><tr><th style={th}>Delivery partner</th><th style={th}>Phone</th><th style={{ ...th, textAlign: 'right' }}>Cash in hand</th><th style={{ ...th, textAlign: 'right' }}>Pending orders</th><th style={th}></th></tr></thead>
              <tbody>
                {partners.map((p) => (
                  <React.Fragment key={p.partnerId}>
                    <tr style={{ background: expanded === p.partnerId ? '#fff7f7' : '#fff' }}>
                      <td style={{ ...td, fontWeight: 600 }}>{p.name}</td>
                      <td style={td}>{p.phone}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{inr(p.cashInHand)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>{p.pendingCount}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button style={{ ...btn, background: expanded === p.partnerId ? theme.primary : '#fff', color: expanded === p.partnerId ? '#fff' : theme.text, borderColor: expanded === p.partnerId ? theme.primary : theme.border }} onClick={() => openPartner(p.partnerId)}>
                          {expanded === p.partnerId ? 'Close' : 'Settle ▸'}
                        </button>
                      </td>
                    </tr>
                    {expanded === p.partnerId && (
                      <tr>
                        <td colSpan={5} style={{ padding: 0, background: '#fafafa' }}>
                          <div style={{ padding: 16 }}>
                            {ordersLoading ? (
                              <div style={{ color: theme.sub }}>Loading orders…</div>
                            ) : orders.length === 0 ? (
                              <div style={{ color: theme.sub }}>No unsettled orders.</div>
                            ) : (
                              <>
                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
                                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                    <input type="checkbox" checked={allSelected} onChange={toggleAll} /> Select all ({orders.length})
                                  </label>
                                  <span style={{ flex: 1 }} />
                                  <button style={{ ...btn, background: '#166534', color: '#fff', borderColor: 'transparent', opacity: busy || selectedIds.length === 0 ? 0.5 : 1 }} disabled={busy || selectedIds.length === 0} onClick={() => markPaid(selectedIds)}>
                                    {busy ? 'Working…' : `✓ Mark selected paid${selectedIds.length ? ` (${selectedIds.length} · ${inr(selectedTotal)})` : ''}`}
                                  </button>
                                  <button style={{ ...btn, background: '#991b1b', color: '#fff', borderColor: 'transparent', opacity: busy ? 0.5 : 1 }} disabled={busy} onClick={() => markPaid(orders.map((o) => o.orderId))}>
                                    Mark ALL paid
                                  </button>
                                </div>
                                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                                  <thead><tr><th style={th}></th><th style={th}>Order #</th><th style={th}>Restaurant</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>Delivered</th></tr></thead>
                                  <tbody>
                                    {orders.map((o) => (
                                      <tr key={o.orderId} style={{ background: selected[o.orderId] ? '#ecfdf5' : 'transparent' }}>
                                        <td style={td}><input type="checkbox" checked={!!selected[o.orderId]} onChange={() => toggle(o.orderId)} /></td>
                                        <td style={{ ...td, fontWeight: 600 }}>{o.orderNumber}</td>
                                        <td style={td}>{o.restaurantName}</td>
                                        <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{inr(o.amount)}</td>
                                        <td style={td}>{fmtDate(o.deliveredAt)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
